import { Box, Link, Stack, Tooltip, Typography } from '@mui/material'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1TextWithTruncation } from 'components/core/TextWithTruncation/C1TextWithTruncation'
import { appEntitlementId } from 'components/pages/entitlement/helpers'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'

const EntitlementLabel: FC<{
    entitlement: AppEntitlement
    showGrantCount?: boolean
}> = ({ entitlement, showGrantCount = true }) => {
    const count = entitlement.grantCount
    const text = (
        <FormattedMessage
            id="components_entitlement_proxy_granted_entitlements_subtitle"
            defaultMessage="{count} grants"
            values={{
                count,
            }}
        />
    )
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box sx={{ overflow: 'hidden', cursor: 'pointer', fontSize: '12px' }}>
                <C1TextWithTruncation
                    text={entitlement.slug || entitlement.displayName}
                />
            </Box>
            {
                showGrantCount && (
                    <Box sx={{ flex: 1, cursor: 'pointer' }}>
                        <Tooltip title={text} placement="top">
                            <Box
                                sx={{
                                    ml: 1,
                                    color: 'grey.800',
                                    backgroundColor: 'white',
                                    fontSize: '10px',
                                    borderRadius: '20px',
                                    px: 1,
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: '10px' }}>
                                    {count}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Box>
                )
            }
        </Box>
    )
}

export const EntitlementChip: FC<{
    appEntitlement: AppEntitlement
    isSelected?: (entitlement: AppEntitlement) => boolean
    showGrantCount?: boolean
}> = ({ appEntitlement, isSelected, showGrantCount }) => {
    let selected = false
    if (isSelected) {
        selected = isSelected(appEntitlement)
    }
    return <C1Chip
        key={appEntitlementId(appEntitlement)}
        color={selected ? 'primary' : 'info'}
        variant="filled"
        label={<EntitlementLabel entitlement={appEntitlement} showGrantCount={showGrantCount} />}
        sx={{ mb: '1px' }}
    />
}

const NUMBER_OF_ENTITLEMENTS_TO_SHOW = 6

export const EntitlementChips: FC<{
    appEntitlements?: AppEntitlement[]
    isSelected?: (entitlement: AppEntitlement) => boolean
    showGrantCount?: boolean
}> = ({ appEntitlements, ...rest }) => {
    const [expanded, setExpanded] = useState(false)

    if (!appEntitlements || appEntitlements.length === 0) {
        return null
    }

    const hasOverflow = appEntitlements?.length > NUMBER_OF_ENTITLEMENTS_TO_SHOW
    const remainingCount = hasOverflow ? (appEntitlements?.length || 0) - NUMBER_OF_ENTITLEMENTS_TO_SHOW : 0
    const entitlementsToShow = expanded ? appEntitlements : appEntitlements?.slice(0, NUMBER_OF_ENTITLEMENTS_TO_SHOW)

    return <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
        {entitlementsToShow?.map((appEntitlement) => (
            <EntitlementChip
                key={appEntitlementId(appEntitlement)}
                appEntitlement={appEntitlement}
                {...rest}
            />
        ))}
        {hasOverflow && (
            <Link
                component="button"
                color="primary"
                underline="hover"
                onClick={(): void => setExpanded((prev) => !prev)}
            >
                <Typography variant="caption" color="primary">
                    {expanded
                        ? <FormattedMessage id="components_resource_entitlements_show_less" defaultMessage="Show less" />
                        : <FormattedMessage id="components_resource_entitlements_show_more" defaultMessage="...{count} more" values={{ count: remainingCount }} />}
                </Typography>
            </Link>
        )}
    </Stack>
}
